import { useCallback, useMemo } from 'react';
import type { TabValue } from '@cbhq/cds-common/tabs/useTabs';
import { Tabs } from '@cbhq/cds-web/tabs/Tabs';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { ORGANIZATION_TAB_ID } from '../utils/constants';

export type EntityTabsLegalEntity = {
  legalEntityId: string;
  legalEntityName: string;
};

type EntityTabsProps = {
  legalEntities: EntityTabsLegalEntity[];
  activeTabId: string;
  onChange: (tabId: string) => void;
  showOrganization: boolean;
};

const messages = defineMessages({
  organizationLabel: {
    id: 'Settings.entityTabs.organizationLabel',
    defaultMessage: 'Organization',
    description: 'Label for the organization-level tab on the workspace Settings page',
  },
  accessibilityLabel: {
    id: 'Settings.entityTabs.accessibilityLabel',
    defaultMessage: 'Select organization or legal entity',
    description: 'Accessibility label for the entity tabs on the workspace Settings page',
  },
});

export function EntityTabs({
  legalEntities,
  activeTabId,
  onChange,
  showOrganization,
}: EntityTabsProps) {
  const { formatMessage } = useIntl();

  const tabItems = useMemo<TabValue<string>[]>(
    () => [
      ...(showOrganization
        ? [
            {
              id: ORGANIZATION_TAB_ID,
              label: formatMessage(messages.organizationLabel),
            },
          ]
        : []),
      ...legalEntities.map(({ legalEntityId, legalEntityName }) => ({
        id: legalEntityId,
        label: legalEntityName,
      })),
    ],
    [formatMessage, legalEntities, showOrganization],
  );

  const activeTab = useMemo(
    () => tabItems.find((tab) => tab.id === activeTabId) ?? tabItems[0],
    [activeTabId, tabItems],
  );

  const handleChange = useCallback(
    (tab: TabValue<string> | null) => {
      onChange(tab?.id ?? tabItems[0]?.id ?? ORGANIZATION_TAB_ID);
    },
    [onChange, tabItems],
  );

  return (
    <Tabs
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      gap={4}
      tabs={tabItems}
      activeTab={activeTab}
      onChange={handleChange}
      testID="settings-entity-tabs"
    />
  );
}
